import React, { useEffect, useRef, useState } from "react";
import { Box, SvgIcon } from "@mui/material";
import * as Yup from "yup";
import { StriimTypography, StriimButton, StriimInputField, StriimForm } from "@striim/striim-ui";
import { AIAssistantGradient } from "src/generic/icon/customIcons";
import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import { selectEngineDetails, setConfigFormData } from "src/stores/redux/features/striim-ai/striim-ai.slice";
import styles from "./ai-config-screen.styles";
import store from "src/stores/redux/store";
import securityService from "core/services/securityService/securityService";
import { AI_AGENT_NAME } from "../../ai-constants";
import copilotService from "core/services/copilotService";
import AIConfigStatusBanner from "../ai-config-status-banner/ai-config-status-banner";

export type AIProvider = string;
export type ConfigMode = "existing" | "launch";

export { AI_AGENT_NAME };

export interface ExistingServerConfig {
    serverUrl: string;
}

export interface NewServerConfig {
    provider: AIProvider;
    model?: string;
    ollamaUrl?: string;
    apiKey?: string;
}

interface AIConfigScreenProps {
    defaultMode?: ConfigMode;
    defaultProvider?: AIProvider;
    isConfigured?: boolean;
    isHealthy?: boolean;
    isMisconfigured?: boolean;
    isError?: boolean;
    healthStatus?: any;
    isDemoServerHealthy?: boolean;
    onConnect: (config: ExistingServerConfig) => void;
    onLaunch: (config: NewServerConfig) => void;
    onTryStriimAIServers?: () => void;
}

interface AIProviderOption {
    value: AIProvider;
    label: string;
}

const AI_PROVIDERS: AIProviderOption[] = [
    { value: "openai", label: "OpenAI" },
    { value: "gemini", label: "Gemini" },
    // { value: "anthropic", label: "Anthropic" },
    { value: "ollama", label: "Ollama" }
];

const PROVIDER_MODEL_PLACEHOLDERS: Record<AIProvider, string> = {
    openai: "e.g. gpt-5.4",
    gemini: "e.g. gemini-3.5-flash",
    anthropic: "e.g. claude-sonnet-4-6",
    ollama: "e.g. qwen3.6:35b"
};

export const SERVER_URL_REGEX = /^https?:\/\/(((localhost)(:\d+))|(([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.[a-zA-Z]{2,6}|(\d{1,3}\.){3}\d{1,3})(:\d+)?)(\/[-a-zA-Z0-9@:%_\+.~#?&//=]*)?$/i;

const existingServerSchema = Yup.object().shape({
    serverUrl: Yup.string()
        .required("Value for Server URL is required")
        .matches(SERVER_URL_REGEX, "Server URL must be a valid URL")
});

const getLaunchSchema = (provider: AIProvider) =>
    provider === "ollama"
        ? Yup.object().shape({
              model: Yup.string().required("Value for AI Model is required"),
              ollamaUrl: Yup.string().required("Value for Ollama URL is Required")
          })
        : Yup.object().shape({
              model: Yup.string().required("Value for AI Model is required"),
              apiKey: Yup.string().required("Value for API key is required")
          });

const NonAdminScreen: React.FC = () => (
    <Box sx={styles.root}>
        <Box sx={{ ...styles.heroSection, mt: "200px" }}>
            <SvgIcon component={AIAssistantGradient as React.ElementType} sx={styles.heroIcon} />
            <StriimTypography sx={styles.headlineText}>{AI_AGENT_NAME} is not configured</StriimTypography>
            <StriimTypography sx={styles.subtitleText}>
                Please contact your Striim Administrator to configure {AI_AGENT_NAME}.
            </StriimTypography>
        </Box>
    </Box>
);

const OptionTab: React.FC<{
    active: boolean;
    label: string;
    subLabel: string;
    onClick: () => void;
    dataTestId?: string;
}> = ({ active, label, subLabel, onClick, dataTestId }) => (
    <Box
        sx={{ ...styles.optionTab, ...(active ? styles.optionTabActive : styles.optionTabInactive) }}
        onClick={onClick}
        data-test-id={dataTestId}
    >
        <StriimTypography
            variant="h4"
            sx={{ ...styles.optionTabLabel, ...(active ? styles.optionTabLabelActive : styles.optionTabLabelInactive) }}
        >
            {label}
        </StriimTypography>
        <StriimTypography variant="body4" sx={styles.optionTabSubLabel}>
            {subLabel}
        </StriimTypography>
    </Box>
);

const ExistingServerForm: React.FC<{
    defaultServerUrl?: string;
    onConnect: (config: ExistingServerConfig) => void;
}> = ({ defaultServerUrl = "", onConnect }) => {
    const formRef = useRef<any>(null);
    const dispatch = useAppDispatch();
    const handleConnect = (data: { serverUrl: string }) => {
        dispatch(
            setConfigFormData({
                mode: "existing",
                serverUrl: data.serverUrl
            })
        );
        onConnect(data);
    };
    return (
        <Box sx={styles.formCard}>
            <StriimForm
                values={{ serverUrl: defaultServerUrl }}
                validationSchema={existingServerSchema}
                formRef={formRef}
                validateOnMount
                validateOnChange
                formProps={{ style: { width: "100%" } }}
            >
                {({ isValid }: { isValid: boolean }) => (
                    <Box sx={styles.fieldStack}>
                        <StriimInputField
                            isFormElement
                            name="serverUrl"
                            label={`${AI_AGENT_NAME} AI Server URL`}
                            placeholder="http://localhost:9090/do/ai"
                            required
                            data-test-id="existing-server-url"
                        />
                        <Box sx={styles.submitButton}>
                            <StriimButton
                                variant="primary"
                                disabled={!isValid}
                                onClick={() => handleConnect({ serverUrl: formRef.current?.values?.serverUrl?.trim() })}
                                data-test-id="connect-button"
                            >
                                Connect
                            </StriimButton>
                        </Box>
                    </Box>
                )}
            </StriimForm>
        </Box>
    );
};

const NewServerForm: React.FC<{
    defaultProvider: AIProvider;
    defaultModel?: string;
    defaultOllamaUrl?: string;
    defaultApiKey?: string;
    onLaunch: (config: NewServerConfig) => void;
}> = ({ defaultProvider, defaultModel = "", defaultOllamaUrl = "", defaultApiKey = "", onLaunch }) => {
    const dispatch = useAppDispatch();
    const [provider, setProvider] = useState<AIProvider>(defaultProvider);
    const formRef = useRef<any>(null);
    const isOllama = provider === "ollama";

    const [providerOptions, setProviderOptions] = useState<AIProviderOption[]>(() => {
        const providerOptionsRes = copilotService.providerConfig;
        return Array.isArray(providerOptionsRes) ? providerOptionsRes : AI_PROVIDERS;
    });

    const handleCreateProvider = (inputValue: string) => {
        const trimmed = inputValue.trim();
        if (!trimmed) return;
        const newOption: AIProviderOption = { value: trimmed, label: trimmed };
        setProviderOptions(prev => (prev.some(o => o.value === trimmed) ? prev : [...prev, newOption]));
        setProvider(trimmed);
    };

    const handleLaunch = () => {
        const vals = formRef.current?.values ?? {};
        dispatch(
            setConfigFormData({
                ...vals,
                provider,
                mode: "launch"
            })
        );
        if (isOllama) {
            onLaunch({ provider, model: vals.model?.trim(), ollamaUrl: vals.ollamaUrl?.trim() });
        } else {
            onLaunch({ provider, model: vals.model?.trim(), apiKey: vals.apiKey?.trim() });
        }
    };

    return (
        <Box sx={styles.formCard}>
            <StriimInputField
                label="AI Provider"
                select
                value={
                    provider
                        ? providerOptions.find(o => o.value === provider) ?? { value: provider, label: provider }
                        : null
                }
                SelectProps={{
                    selectVariant: "creatable",
                    options: providerOptions,
                    isClearable: false,
                    onCreateOption: handleCreateProvider,
                    dataTestId: "provider-select"
                }}
                onChange={(option: { value: AIProvider } | null) => {
                    if (option) setProvider(option.value);
                }}
            />

            {/* key forces remount when provider changes so fields reset */}
            <StriimForm
                key={provider}
                values={
                    isOllama
                        ? { ollamaUrl: defaultOllamaUrl, model: defaultModel }
                        : { model: defaultModel, apiKey: defaultApiKey }
                }
                validationSchema={getLaunchSchema(provider)}
                formRef={formRef}
                validateOnMount
                validateOnChange
                formProps={{ style: { width: "100%" } }}
            >
                {({ isValid }: { isValid: boolean }) => (
                    <Box sx={styles.fieldStack}>
                        {isOllama ? (
                            <>
                                <StriimInputField
                                    isFormElement
                                    name="model"
                                    label="AI Model"
                                    placeholder="qwen2.5-coder:32b"
                                    required
                                    data-test-id="ollama-model"
                                />
                                <StriimInputField
                                    isFormElement
                                    name="ollamaUrl"
                                    label="Ollama URL"
                                    placeholder="http://localhost:11434"
                                    required
                                    data-test-id="ollama-url"
                                />
                            </>
                        ) : (
                            <>
                                <StriimInputField
                                    isFormElement
                                    name="model"
                                    label="AI Model"
                                    placeholder={PROVIDER_MODEL_PLACEHOLDERS[provider]}
                                    required
                                    data-test-id="model-input"
                                />
                                <StriimInputField
                                    isFormElement
                                    name="apiKey"
                                    label="API Key"
                                    type="password"
                                    previewPassword
                                    required
                                    data-test-id="api-key-input"
                                />
                            </>
                        )}
                        <Box sx={styles.submitButton}>
                            <StriimButton
                                variant="primary"
                                disabled={!isValid}
                                onClick={handleLaunch}
                                data-test-id="launch-button"
                            >
                                Launch
                            </StriimButton>
                        </Box>
                    </Box>
                )}
            </StriimForm>
        </Box>
    );
};

const AIConfigScreen: React.FC<AIConfigScreenProps> = ({
    defaultMode = "launch",
    defaultProvider = "openai",
    isConfigured = false,
    isHealthy = true,
    isMisconfigured = false,
    isError = false,
    healthStatus,
    isDemoServerHealthy = true,
    onConnect,
    onLaunch,
    onTryStriimAIServers
}) => {
    const [mode, setMode] = useState<ConfigMode>(defaultMode);
    const [provider, setProvider] = useState<AIProvider>(defaultProvider);
    const [model, setModel] = useState<string>("");
    const [ollamaUrl, setOllamaUrl] = useState<string>("");
    const [serverUrl, setServerUrl] = useState<string>("");
    const [apiKey, setApiKey] = useState<string>("");
    const engineDetails = useAppSelector(selectEngineDetails);
    useEffect(() => {
        if (!isConfigured || !engineDetails) {
            // get pre filled details from store
            const data = store.getState().striimAi.configFormData;
            data.provider && setProvider(data.provider);
            data.mode && setMode(data.mode);
            data.model && setModel(data.model);
            data.serverUrl && setServerUrl(data.serverUrl);
            data.ollamaUrl && setOllamaUrl(data.ollamaUrl);
            data.apiKey && setApiKey(data.apiKey);
        } else {
            if (engineDetails.engineType) {
                setMode(engineDetails.engineType === "LOCAL" ? "launch" : "existing");
            }
            if (engineDetails.provider) setProvider(engineDetails.provider as AIProvider);
            if (engineDetails.model) setModel(engineDetails.model);
            if (engineDetails.ollamaURL) setOllamaUrl(engineDetails.ollamaURL);
            if (engineDetails.striimAiUrl && engineDetails.engineType === "existing")
                setServerUrl(engineDetails.striimAiUrl);
        }
    }, [isConfigured, engineDetails]);

    if (!securityService.isAdmin()) {
        return <NonAdminScreen />;
    }

    return (
        <Box sx={styles.root} data-test-id="configure-ai-container">
            <AIConfigStatusBanner
                isConfigured={isConfigured}
                isHealthy={isHealthy}
                isMisconfigured={isMisconfigured}
                isError={isError}
                healthStatus={healthStatus}
                isDemoServerHealthy={isDemoServerHealthy}
                onTryStriimAIServers={onTryStriimAIServers}
            />
            <Box sx={styles.heroSection}>
                <SvgIcon component={AIAssistantGradient as React.ElementType} sx={styles.heroIcon} />
                <StriimTypography sx={styles.headlineText}>
                    {isConfigured ? `Update ${AI_AGENT_NAME}'s configuration` : `${AI_AGENT_NAME} is not configured`}
                </StriimTypography>
                <StriimTypography sx={styles.subtitleText}>
                    {isConfigured
                        ? "Update your configuration or connect to a different Clara AI Server."
                        : "Launch a new Clara Server locally, or connect to an existing one."}
                </StriimTypography>
            </Box>

            <Box sx={styles.optionToggle}>
                <OptionTab
                    active={mode === "launch"}
                    label="Launch AI Server Locally"
                    subLabel="Launch a Clara AI Server on the same host using Docker"
                    onClick={() => setMode("launch")}
                    dataTestId="tab-launch"
                />
                <OptionTab
                    active={mode === "existing"}
                    label="Use Existing AI Server"
                    subLabel="Use a preconfigured Clara AI Server"
                    onClick={() => setMode("existing")}
                    dataTestId="tab-existing"
                />
            </Box>
            <Box sx={styles.maxWidthContainer}>
                {mode === "existing" ? (
                    <ExistingServerForm key={serverUrl} defaultServerUrl={serverUrl} onConnect={onConnect} />
                ) : (
                    <NewServerForm
                        key={`${provider}-${model}-${ollamaUrl}`}
                        defaultProvider={provider}
                        defaultModel={model}
                        defaultOllamaUrl={ollamaUrl}
                        onLaunch={onLaunch}
                        defaultApiKey={apiKey}
                    />
                )}
            </Box>

            <Box sx={styles.footerNoteStack}>
                <StriimTypography sx={styles.footerNote}>
                    AI can make mistakes, please verify the plans before executing.
                </StriimTypography>
                <StriimTypography sx={styles.footerNote}>
                    Need help? See the Striim documentation for <i>{AI_AGENT_NAME} AI Agent </i>.
                </StriimTypography>
            </Box>
        </Box>
    );
};

export default AIConfigScreen;
